import { styled } from 'styled-components';
import { getFilterByType } from '@/constants/filterType';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  /* Outermost Project wrapper. min-width:0 stops this flex item (and the flex
     chain below) from being forced wide by a slider's intrinsic track width;
     overflow-x:hidden is the belt-and-braces catch so nothing inside Project can
     open a page-level horizontal scrollbar (the white gutter beside the map).
     Height stays content-driven, so this creates no vertical scroll container. */
  min-width: 0;
  overflow-x: hidden;
`;

export const TitleBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const Title = styled.h1`
  text-align: start;
  font-size: 40px;
  font-weight: 800;
  text-transform: uppercase;
  margin: 30px 0;

  b {
    color: #e5007d;
  }

  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    font-size: 30px;
    margin: 15px 0 50px 0;

    b {
      font-size: 40px;
    }
  }

  @media (max-width: 600px) {
    font-size: 20px;
    margin: 15px 0;

    b {
      font-size: 25px;
    }
  }
`;

export const MobileShare = styled.div`
  cursor: pointer;
  display: none;
  justify-content: center;
  gap: 20px;
  color: #adaba8;

  @media (max-width: 800px) {
    display: flex;

    img {
      width: 30px;
      margin-right: 20px;
    }
  }
`;

export const Description = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  width: 100%;
  /* min-width:0: flex items in this chain must be allowed to shrink so a wide
     descendant (slider track / row) can't push the row past the viewport. */
  min-width: 0;
  margin-bottom: 30px;
  border-top: 1px solid #e8e6e2;
  padding-top: 10px;

  @media (max-width: 800px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const LeftDescription = styled.div`
  display: flex;
  justify-content: space-between;
  width: 15%;

  @media (max-width: 1400px) {
    width: 30%;
    img {
      width: 20px;
    }
  }

  @media (max-width: 800px) {
    display: none;
  }
`;

export const BackMapButton = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  color: #e5007d;
  gap: 20px;
`;

export const ShareButton = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  gap: 20px;
  color: #adaba8;
`;

export const DescriptionBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  border-right: 1px solid #e8e6e2;
  padding-right: 40px;

  @media (max-width: 800px) {
    padding: 5px 10px;
    border: 1px solid #e8e6e2;
    border-radius: 10px;
  }
`;

export const LeftDescriptionBlock = styled.img<{ type?: string }>`
  width: 30px;
  height: 30px;
  ${({ type }) => getFilterByType(type)}
`;

export const RightDescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 800px) {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }
`;

export const DescriptionBlockTitle = styled.div`
  font-weight: 800;
  font-size: 20px;
`;

export const DescriptionBlockText = styled.div`
  /* Was "text-transform: capitalize" until 2026-09-05 (deprecated in place, §2).
     Tile labels are now authored in their final casing ("kg CO2 sequestered",
     "t CO2 sequestered (2025)"), which capitalize would turn into "Kg CO2
     Sequestered". "none" is needed explicitly because the surrounding
     Description block sets uppercase, which the label would otherwise inherit.
     All tile labels therefore render exactly as written, in sentence case. */
  text-transform: none;
`;

export const RightDescription = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 50px;
  width: 70%;
  min-width: 0;
  flex-wrap: wrap;

  @media (max-width: 800px) {
    justify-content: center;
    width: 100%;
    gap: 20px;
  }
`;

export const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  margin-bottom: 30px;
`;
export const MainImage = styled.img`
  width: 100%;
  height: 500px;
  border-radius: 15px;
  margin-bottom: 20px;
  object-fit: cover;
`;

export const ThumbnailsContainer = styled.div`
  width: 80%;
  display: flex;
  gap: 10px;
`;
export const Thumbnail = styled.img<{ isSelected: boolean }>`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 10px;
  cursor: pointer;
  border: ${({ isSelected }) => (isSelected ? '1px solid #e5007d' : '1px solid gray')};
  opacity: ${({ isSelected }) => (isSelected ? 1 : 0.7)};
  &:hover {
    opacity: 1;
  }
`;
export const TabsContainer = styled.div`
  width: 100%;
  min-width: 0;
  background-color: #f0efec;
  border-radius: 20px;
  padding: 10px 0;
`;
export const Tabs = styled.div`
  width: 100%;
  min-width: 0;
  position: sticky;
  background-color: #f0efec;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-around;
`;

export const Tab = styled.div<{ active: boolean; isFactory: boolean }>`
  width: ${({ isFactory }) => (isFactory ? '33.333%' : '25%')};
  padding: 20px;
  border-bottom: ${({ active }) => (active ? '2px solid #e5007d' : '1px solid #ADABA8')};
  color: ${({ active }) => (active ? '#e5007d' : '#ADABA8')};
  cursor: pointer;
  display: flex;
  justify-content: center;
  transition: background-color 0.3s ease-in-out;
  font-size: 1.1rem;
  text-transform: uppercase;

  &:hover {
    border-bottom: 2px solid #e5007d;
    color: #e5007d;
  }
`;

export const Section = styled.section`
  padding: 50px;
  height: 100%;
  margin: 20px;
  border-bottom: 1px solid #d9d6cf;

  &:last-child {
    border-bottom: none;
  }

  & .MuiPaginationItem-root.Mui-selected {
    background-color: #e5007d !important;
  }

  @media (max-width: 500px) {
    padding: 10px;
  }
`;
export const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 30px;
`;
export const ImpactDescription = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 100px;
  min-width: 0;
  color: #6d6b68;

  p {
    font-family: 'Roboto', sans-serif;
    line-height: 24px;
  }

  div {
    width: 50%;
  }

  img {
    width: 100%;
  }

  @media (max-width: 1000px) {
    flex-direction: column;
    gap: 10px;
    div {
      width: 100%;
    }
    img {
      width: 100%;
    }
  }
`;
export const FarmersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
  justify-content: space-between;

  @media (max-width: 1000px) {
    justify-content: center;
  }
`;

// Was styled.p until 2026-09-05. The intro is now rendered through <RichText>,
// which emits its own <p> per paragraph, and <p> inside <p> is invalid HTML.
// Same styles, so the text looks exactly as before.
export const BackgroundIntro = styled.div`
  font-family: 'Roboto', sans-serif;
  line-height: 24px;
  margin: 30px 0;
  width: 100%;
`;

export const LIRPReport = styled.a`
  display: block;
  width: 100%;
  font-family: 'Roboto', sans-serif;
  line-height: 24px;
  color: #e5007d;
  margin: 30px 0;
  text-transform: uppercase;

  &:hover {
    color: #000;
  }
`;
